import React, {type RefObject} from 'react';

interface MegaphoneProps {
    /**
     * A string representing the CSS class to be applied to the CheckIcon element to override its default styling.
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     */
    color1?: string;
    /**
     * A number or string representing the height of the CheckIcon element.
     */
    height?: number | string;
    /**
     * The `ref` property represents a reference to the SVG element, allowing for direct manipulation or access to the DOM node.
     */
    ref?: RefObject<SVGSVGElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     */
    testId?: string;
    /**
     * A number or string representing the height of the CheckIcon element.
     */
    width?: number | string;
}

/**
 * The `Check` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `CheckProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.className A string representing the CSS class to be applied to the CheckIcon element to override its default styling.
 * @param props.color1    A string representing the color to be applied to the Icon.
 * @param props.height    A number or string representing the height of the CheckIcon element.
 * @param props.ref       A reference to the SVG element, allowing for direct manipulation or access to the DOM node.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the CheckIcon element.
 * @returns A React element representing the `Check` component with the specified properties.
 *
 * @example
 * ```tsx
 * const CheckComponent = <Check className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const Megaphone = ({
    className = '',
    color1 = '#0019A5',
    height = 24,
    ref,
    testId = 'Megaphone',
    width = 24
}: MegaphoneProps) => (
    <svg
        ref={ref}
        className={className}
        data-cy={testId}
        fill="none"
        height={height}
        viewBox="0 0 24 24"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            // eslint-disable-next-line @stylistic/max-len
            d="M20.25 3.99997C20.25 3.95355 20.2373 3.90763 20.2129 3.86814C20.1885 3.82873 20.1528 3.79707 20.1113 3.77634C20.0699 3.75572 20.0236 3.74679 19.9775 3.75095C19.9313 3.75512 19.8867 3.77192 19.8496 3.79978C17.4864 5.66375 14.5817 6.69784 11.583 6.75193L10.9805 6.74997H8.75V13.25H10.9805C14.1908 13.1673 17.3285 14.2116 19.8496 16.2002L19.9102 16.2334C19.9317 16.2417 19.9544 16.2469 19.9775 16.249C20.0236 16.2532 20.0699 16.2442 20.1113 16.2236C20.1528 16.2029 20.1885 16.1712 20.2129 16.1318C20.2373 16.0923 20.25 16.0464 20.25 16V3.99997ZM6.77734 14.75C6.92218 16.9177 7.69003 19.0033 9 20.75C9.19891 21.0152 9.49505 21.1904 9.82324 21.2373C10.1514 21.2842 10.4848 21.1989 10.75 21C11.0152 20.8011 11.1904 20.5049 11.2373 20.1767C11.2842 19.8485 11.1989 19.5152 11 19.25C10.0147 17.9363 9.4231 16.3761 9.2832 14.75H6.77734ZM3.75 12C3.75 12.3315 3.88179 12.6493 4.11621 12.8838C4.35063 13.1182 4.66848 13.25 5 13.25H7.25V6.74997H5C4.66848 6.74997 4.35063 6.88177 4.11621 7.11619C3.88179 7.35061 3.75 7.66845 3.75 7.99997V12ZM21.75 16C21.75 16.325 21.6591 16.6434 21.4883 16.9199C21.3174 17.1963 21.0729 17.4201 20.7822 17.5654C20.4916 17.7106 20.1663 17.7723 19.8428 17.7431C19.5192 17.714 19.2101 17.5952 18.9502 17.4004C18.9451 17.3965 18.9396 17.3926 18.9346 17.3886C16.6862 15.6101 13.8854 14.6762 11.0195 14.75H10.79C10.9253 16.0508 11.4099 17.2958 12.2002 18.3496C12.6377 18.933 12.8257 19.6667 12.7227 20.3886C12.6195 21.1106 12.2338 21.7626 11.6504 22.2002C11.067 22.6377 10.3333 22.8257 9.61133 22.7226C8.88938 22.6195 8.23741 22.2338 7.7998 21.6504C6.29504 19.644 5.42227 17.2426 5.27539 14.75H5C4.27065 14.75 3.57139 14.46 3.05566 13.9443C2.53994 13.4286 2.25 12.7293 2.25 12V7.99997C2.25 7.27063 2.53994 6.57136 3.05566 6.05564C3.57139 5.53991 4.27065 5.24997 5 5.24997H11.0195C13.8854 5.32375 16.6862 4.38987 18.9346 2.6113L18.9502 2.59958C19.2101 2.40473 19.5192 2.28599 19.8428 2.25681C20.1663 2.22764 20.4916 2.28934 20.7822 2.43454C21.0729 2.57987 21.3174 2.80363 21.4883 3.08005C21.6591 3.35651 21.75 3.67498 21.75 3.99997V16Z"
            fill={color1}
        />
    </svg>
);

Megaphone.displayName = 'Check';